import { useState, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Loader2, LogOut, Check, Building2, User, Camera, Delete } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SupabaseAuthProvider, useSbAuth } from "@/context/SupabaseAuthContext";
import { ProtectedRoute, RequireRestaurant, RequirePermission } from "@/components/auth/guards";
import { PageHeader } from "@/components/library/layout";
import { Badge } from "@/components/library/badges";
import WorkspaceShell from "@/components/workspace/WorkspaceShell";
import Home from "@/pages/app/Home";
import Operation from "@/pages/app/Operation";
import Department from "@/pages/app/Department";
import Admin from "@/pages/app/Admin";
import Foodcost from "@/pages/app/Foodcost";
import Ledning from "@/pages/app/Ledning";
import LiveDashboard from "@/pages/app/LiveDashboard";
import { pinLogin, fetchRestaurantsPublic } from "@/lib/pinAuth";
import { uploadAvatar } from "@/lib/storage";

function AuthFrame({ title, overline, children, footer }) {
  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex flex-col items-center text-center mb-7">
          <img src="/logo.png" alt="Restaurangkollen" className="h-14 w-14 rounded-full object-cover mb-3" />
          <span className="font-display text-xl font-bold tracking-tight">Restaurangkollen</span>
        </div>
        <div className="bg-card border border-border rounded-2xl shadow-[0_10px_50px_-16px_rgba(20,20,20,0.15)] p-7 sm:p-8">
          {overline && <p className="overline text-muted-foreground mb-2">{overline}</p>}
          <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{title}</h2>
          {children}
          {footer}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6 tracking-wide">Drift, kvalitet och uppföljning.</p>
      </div>
    </div>
  );
}

function PinPad({ value, onChange }) {
  const press = (d) => { if (value.length < 4) onChange(value + d); };
  const back = () => onChange(value.slice(0, -1));
  const keyBtn = "h-14 rounded-xl bg-card border border-border text-xl font-display font-semibold hover:border-accent active:scale-95 transition-all duration-150";
  return (
    <div className="space-y-4" data-testid="pin-pad">
      <div className="flex justify-center gap-3">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-4 w-4 rounded-full border-2 transition-colors ${i < value.length ? "bg-accent border-accent" : "border-border"}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button type="button" key={d} onClick={() => press(d)} data-testid={`pinpad-${d}`} className={keyBtn}>{d}</button>
        ))}
        <span />
        <button type="button" onClick={() => press("0")} data-testid="pinpad-0" className={keyBtn}>0</button>
        <button type="button" onClick={back} data-testid="pinpad-back" className="h-14 rounded-xl border border-border grid place-items-center text-muted-foreground hover:border-accent hover:text-foreground active:scale-95 transition-all duration-150"><Delete className="h-5 w-5" /></button>
      </div>
    </div>
  );
}

function Login() {
  const { signIn } = useSbAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("pin"); // 'pin' | 'email'
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const [initials, setInitials] = useState(""); const [pin, setPin] = useState(""); const [pinErr, setPinErr] = useState("");
  const [device, setDevice] = useState(() => { try { return JSON.parse(localStorage.getItem("rk_device_restaurant")); } catch (_) { return null; } });
  const [rests, setRests] = useState([]);
  const [staffGreeting] = useState(() => {
    const lines = ["Ett grymt team börjar med dig. Logga in.", "Ni gör skillnad varje dag. Välkommen in!"];
    return lines[Math.floor(Math.random() * lines.length)];
  });

  useEffect(() => {
    if (mode === "pin" && !device) fetchRestaurantsPublic().then(setRests).catch(() => {});
  }, [mode, device]);

  const submitEmail = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    const r = await signIn(email, password); setLoading(false);
    if (r.ok) nav("/konto"); else setError(r.error);
  };
  const submitPin = async (e) => {
    e?.preventDefault(); setPinErr("");
    if (!device) { setPinErr("Välj restaurang först"); return; }
    setLoading(true);
    try { await pinLogin(device.id, initials, pin); nav("/konto"); }
    catch (err) { setPinErr(err.message || "Fel initialer eller PIN"); setPin(""); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (mode === "pin" && device && initials.trim() && pin.length === 4 && !loading) submitPin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);
  const pickDevice = (r) => { localStorage.setItem("rk_device_restaurant", JSON.stringify(r)); setDevice(r); };

  return (
    <AuthFrame title={mode === "pin" ? staffGreeting : "Välkommen tillbaka"}>
      {mode === "pin" ? (
        <div className="space-y-5">
          {!device ? (
            <div className="space-y-3" data-testid="pin-device-picker">
              <Label>Välj din restaurang (sparas på denna enhet)</Label>
              {rests.map((r) => (
                <button key={r.id} type="button" onClick={() => pickDevice(r)} data-testid={`pin-rest-${r.id}`}
                  className="w-full flex items-center gap-3 bg-card border border-border rounded-xl p-3 hover:border-accent hover:-translate-y-0.5 transition-all duration-300">
                  <span className="h-8 w-8 grid place-items-center rounded-lg bg-accent text-white"><Building2 className="h-4 w-4" /></span>
                  <span className="font-medium flex-1 text-left">{r.name}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
              {rests.length === 0 && <p className="text-sm text-muted-foreground">Laddar restauranger…</p>}
            </div>
          ) : (
            <form onSubmit={submitPin} className="space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Enhet: <span className="text-foreground font-medium">{device.name}</span></span>
                <button type="button" onClick={() => { localStorage.removeItem("rk_device_restaurant"); setDevice(null); }} className="hover:text-foreground underline" data-testid="pin-change-device">Byt</button>
              </div>
              <div className="space-y-1.5"><Label>Initialer</Label>
                <Input value={initials} onChange={(e) => setInitials(e.target.value.toUpperCase())} placeholder="T.ex. AL" maxLength={4} required data-testid="pin-initials" className="uppercase tracking-widest" /></div>
              <div className="space-y-2"><Label>4-siffrig PIN</Label>
                <PinPad value={pin} onChange={setPin} /></div>
              {pinErr && <p className="text-sm text-[hsl(0_80%_72%)]" data-testid="pin-error">{pinErr}</p>}
              <Button type="submit" disabled={loading || pin.length !== 4 || !initials} className="w-full bg-primary text-primary-foreground hover:opacity-90 group" data-testid="pin-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Logga in <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" /></>}
              </Button>
            </form>
          )}
          <button type="button" onClick={() => setMode("email")} className="text-sm text-accent hover:underline" data-testid="switch-to-email">Logga in med e-post istället</button>
        </div>
      ) : (
        <>
          <form onSubmit={submitEmail} className="space-y-4">
            <div className="space-y-1.5"><Label>E-post</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="exempel@mail.se" required data-testid="sb-email" /></div>
            <div className="space-y-1.5"><Label>Lösenord</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="sb-password" /></div>
            {error && <p className="text-sm text-[hsl(0_80%_72%)]" data-testid="sb-login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:opacity-90 group" data-testid="sb-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Logga in <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" /></>}
            </Button>
            <div className="flex justify-between text-sm">
              <Link to="/konto/glomt" className="text-muted-foreground hover:text-foreground">Glömt lösenord?</Link>
              <Link to="/konto/registrera" className="text-accent hover:underline">Skapa konto</Link>
            </div>
          </form>
          <button type="button" onClick={() => setMode("pin")} className="mt-6 text-sm text-accent hover:underline" data-testid="switch-to-pin">← Tillbaka till snabbinloggning</button>
        </>
      )}
    </AuthFrame>
  );
}

function Register() {
  const { signUp } = useSbAuth(); const nav = useNavigate();
  const [form, setForm] = useState({ fullName: "", email: "", password: "" });
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    const r = await signUp(form); setLoading(false);
    if (r.ok) nav("/konto"); else setError(r.error);
  };
  return (
    <AuthFrame overline="Skapa konto" title="Kom igång">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>Namn</Label><Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required data-testid="sb-reg-name" /></div>
        <div className="space-y-1.5"><Label>E-post</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="sb-reg-email" /></div>
        <div className="space-y-1.5"><Label>Lösenord</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="sb-reg-password" /></div>
        {error && <p className="text-sm text-[hsl(0_80%_72%)]">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:opacity-90" data-testid="sb-reg-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Skapa konto"}
        </Button>
        <p className="text-sm text-muted-foreground">Har du redan konto? <Link to="/konto/login" className="text-accent hover:underline">Logga in</Link></p>
      </form>
    </AuthFrame>
  );
}

function Forgot() {
  const { requestPasswordReset } = useSbAuth();
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const r = await requestPasswordReset(email); setLoading(false);
    if (r.ok) setSent(true); else toast.error(r.error);
  };
  return (
    <AuthFrame overline="Återställning" title="Glömt lösenord">
      {sent ? (
        <div className="space-y-4" data-testid="sb-forgot-sent">
          <div className="flex items-center gap-2 text-[hsl(145_60%_60%)]"><Check className="h-5 w-5" /> Återställningslänk skickad</div>
          <p className="text-sm text-muted-foreground">Kolla din inkorg och följ länken för att välja nytt lösenord.</p>
          <Link to="/konto/login"><Button variant="secondary" className="w-full">Tillbaka till inloggning</Button></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>E-post</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="sb-forgot-email" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:opacity-90" data-testid="sb-forgot-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Skicka återställningslänk"}
          </Button>
          <Link to="/konto/login" className="block text-sm text-muted-foreground hover:text-foreground">Tillbaka</Link>
        </form>
      )}
    </AuthFrame>
  );
}

function ResetPassword() {
  const { updatePassword } = useSbAuth(); const nav = useNavigate();
  const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const r = await updatePassword(password); setLoading(false);
    if (r.ok) { toast.success("Lösenord uppdaterat"); nav("/konto"); } else toast.error(r.error);
  };
  return (
    <AuthFrame overline="Nytt lösenord" title="Återställ lösenord">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label>Nytt lösenord</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="sb-reset-password" /></div>
        <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:opacity-90" data-testid="sb-reset-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Spara lösenord"}
        </Button>
      </form>
    </AuthFrame>
  );
}

function SelectContext() {
  const { restaurants, organizations, currentRestaurant, selectRestaurant, signOut } = useSbAuth(); const nav = useNavigate();
  useEffect(() => {
    if (currentRestaurant) { nav("/konto", { replace: true }); return; }
    if (restaurants.length === 1) { selectRestaurant(restaurants[0].id); nav("/konto", { replace: true }); }
  }, [currentRestaurant, restaurants, selectRestaurant, nav]);
  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-md animate-fade-up">
        <p className="overline text-muted-foreground mb-2">{organizations[0]?.name}</p>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Välj restaurang</h2>
        <p className="text-sm text-muted-foreground mb-6">Du har åtkomst till flera enheter. Välj vilken du vill arbeta i.</p>
        <div className="space-y-3">
          {restaurants.map((r) => (
            <button key={r.id} onClick={() => { selectRestaurant(r.id); nav("/konto"); }} data-testid={`sb-pick-${r.id}`}
              className="w-full flex items-center gap-3 bg-card border border-border rounded-2xl p-4 hover:border-accent hover:-translate-y-0.5 transition-all duration-300">
              <span className="h-10 w-10 grid place-items-center rounded-xl bg-accent text-white"><Building2 className="h-5 w-5" /></span>
              <span className="font-display font-semibold flex-1 text-left">{r.name}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
        <button onClick={async () => { await signOut(); nav("/konto/login"); }} className="mt-6 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5">
          <LogOut className="h-4 w-4" /> Logga ut
        </button>
      </div>
    </div>
  );
}

function Profile() {
  const { profile, role, roleLabel, updatePassword, signOut } = useSbAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState(""); const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState(profile?.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setAvatar(profile?.avatar_url || ""); }, [profile]);
  const change = async (e) => {
    e.preventDefault(); if (!pw) return; setLoading(true);
    const r = await updatePassword(pw); setLoading(false);
    if (r.ok) { toast.success("Lösenord uppdaterat"); setPw(""); } else toast.error(r.error);
  };
  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadAvatar(file); setAvatar(url); toast.success("Profilbild uppdaterad"); }
    catch (err) { toast.error(err.message || "Uppladdning misslyckades"); }
    finally { setUploading(false); }
  };
  return (
    <div className="max-w-xl mx-auto p-6 sm:p-10 space-y-8" data-testid="sb-profile">
        <PageHeader overline="Konto" title="Min profil" />
        <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4">
          <button type="button" onClick={() => fileRef.current?.click()} className="relative group h-14 w-14 shrink-0 rounded-full overflow-hidden bg-accent grid place-items-center" data-testid="avatar-upload-btn" title="Byt profilbild">
            {avatar
              ? <img src={avatar} alt="Profilbild" className="h-full w-full object-cover" />
              : <span className="text-white text-lg font-bold">{(profile?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</span>}
            <span className="absolute inset-0 bg-black/50 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
              {uploading ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Camera className="h-4 w-4 text-white" />}
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickAvatar} className="hidden" data-testid="avatar-file-input" />
          <div>
            <p className="font-display text-lg font-bold">{profile?.full_name}</p>
            <div className="flex items-center gap-2 mt-1"><Badge tone="accent">{roleLabel}</Badge></div>
          </div>
        </div>
        <form onSubmit={change} className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <p className="font-display font-semibold">Byt lösenord</p>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nytt lösenord" data-testid="profile-new-password" />
          <Button type="submit" disabled={loading} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="profile-save-password">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Spara"}
          </Button>
        </form>
        <button onClick={async () => { await signOut(); nav("/konto/login"); }} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5">
          <LogOut className="h-4 w-4" /> Logga ut
        </button>
    </div>
  );
}

export default function Konto() {
  return (
    <SupabaseAuthProvider>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="registrera" element={<Register />} />
        <Route path="glomt" element={<Forgot />} />
        <Route path="aterstall" element={<ResetPassword />} />
        <Route path="valj" element={<ProtectedRoute><SelectContext /></ProtectedRoute>} />
        <Route element={<ProtectedRoute><RequireRestaurant><WorkspaceShell /></RequireRestaurant></ProtectedRoute>}>
          <Route path="" element={<Home />} />
          <Route path="live" element={<LiveDashboard />} />
          <Route path="operation" element={<Operation />} />
          <Route path="operation/:slug" element={<Department />} />
          <Route path="roller" element={<RequirePermission permission="user.read"><Admin /></RequirePermission>} />
          <Route path="foodcost" element={<RequirePermission permission="foodcost.access"><Foodcost /></RequirePermission>} />
          <Route path="ledning" element={<RequirePermission permission="dashboard.access"><Ledning /></RequirePermission>} />
          <Route path="profil" element={<Profile />} />
        </Route>
        <Route path="*" element={<Navigate to="/konto" replace />} />
      </Routes>
    </SupabaseAuthProvider>
  );
}
